import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/ApiKeyContext';
import { 
  PictureAsPdf, 
  Description, 
  Subject, 
  Image as ImageIcon,
  InsertDriveFile,
  Visibility as VisibilityIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Typography,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

// URL base da API - usa variável de ambiente ou fallback para produção
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Função para truncar nome do arquivo mantendo a extensão
const truncateFilename = (filename, maxLength = 25) => {
  if (!filename || filename.length <= maxLength) {
    return filename;
  }
  
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // Sem extensão
    return filename.substring(0, maxLength - 3) + '...';
  }
  
  const extension = filename.substring(lastDotIndex);
  const nameWithoutExtension = filename.substring(0, lastDotIndex);
  
  // Se a extensão é muito longa para o espaço disponível, truncar tudo
  if (extension.length >= maxLength - 3) {
    return filename.substring(0, maxLength - 3) + '...';
  }
  
  // Calcular espaço disponível para o nome (reservando espaço para extensão + "...")
  const ellipsisLength = 3;
  const availableForName = maxLength - extension.length - ellipsisLength;
  
  if (availableForName <= 0) {
    // Se não há espaço suficiente para nome + "..." + extensão, mostrar só extensão
    return '...' + extension;
  }
  
  return nameWithoutExtension.substring(0, availableForName) + '...' + extension;
};

const useStyles = makeStyles((theme) => ({
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.25),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    padding: theme.spacing(0.25),
    borderRadius: theme.spacing(0.25),
  },
  fileItemNoLink: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.25),
    padding: theme.spacing(0.25),
    borderRadius: theme.spacing(0.25),
    cursor: 'default',
  },
  fileIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
    color: 'var(--ikaro-orange-primary)',
  },
  previewIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
    color: 'var(--ikaro-orange-hover)',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--ikaro-status-running-text)',
    },
  },
  fileName: {
    fontSize: '0.75rem',
    textDecoration: 'none',
    color: 'var(--ikaro-orange-primary)',
    // fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  fileNameNoLink: {
    fontSize: '0.75rem',
    color: theme.palette.text.primary,
  },
  floatingAlert: {
    position: 'fixed',
    top: 16,
    right: 16,
    zIndex: 9999,
    minWidth: '300px',
    maxWidth: '500px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    borderRadius: '4px',
    animation: '$slideIn 0.3s ease-out',
  },
  '@keyframes slideIn': {
    from: {
      transform: 'translateX(100%)',
      opacity: 0,
    },
    to: {
      transform: 'translateX(0)',
      opacity: 1,
    },
  },
  previewDialog: {
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '900px',
      height: '700px',
    },
  },
  previewContent: {
    height: '100%',
    overflow: 'hidden',
    padding: theme.spacing(2),
    '& iframe': {
      width: '100%',
      height: '100%',
      border: 'none',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
}));

const FileDownload = ({ 
  file, 
  filename, 
  token: fileToken,
  html_preview_token,
  className,
  style,
  showError = true
}) => {
  const classes = useStyles();
  const { token } = useAuth();
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Determinar filename, token e html_preview_token a partir dos props
  const finalFilename = file?.filename || filename;
  const finalToken = file?.token || fileToken;
  const finalHtmlPreviewToken = file?.html_preview_token || html_preview_token;

  // Limpar erro automaticamente após 5 segundos
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Função para obter o ícone apropriado baseado na extensão do arquivo
  const getFileIcon = (filename) => {
    if (!filename) return <InsertDriveFile className={classes.fileIcon} />;
    
    const extension = filename.split('.').pop().toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <PictureAsPdf className={classes.fileIcon} />;
      case 'docx':
      case 'doc':
        return <Description className={classes.fileIcon} />;
      case 'txt':
        return <Subject className={classes.fileIcon} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        return <ImageIcon className={classes.fileIcon} />;
      default:
        return <InsertDriveFile className={classes.fileIcon} />;
    }
  };

  // Função para fazer download do arquivo
  const handleDownload = async (fileToken, filename) => {
    if (!fileToken || !token) return;
    
    try {
      setError(null);
      setDownloadingFile(fileToken);
      
      const downloadUrl = `${API_BASE_URL}/files/download?token=${fileToken}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/octet-stream',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao fazer download do arquivo: ${response.status} - ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'arquivo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      setError(`Falha ao fazer download do arquivo "${filename}": ${error.message}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  // Função para buscar o conteúdo HTML do preview
  const handlePreview = async (htmlPreviewToken, filename) => {
    if (!htmlPreviewToken || !token) return;
    
    try {
      setError(null);
      setLoadingPreview(true);
      setPreviewOpen(true);
      
      const previewUrl = `${API_BASE_URL}/files/download?token=${htmlPreviewToken}`;
      
      const response = await fetch(previewUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'text/html',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar preview do arquivo: ${response.status} - ${response.statusText}`);
      }
      
      const htmlContent = await response.text();
      setPreviewContent(htmlContent);
      
    } catch (error) {
      console.error('Erro ao buscar preview:', error);
      setError(`Falha ao carregar preview do arquivo "${filename}": ${error.message}`);
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Função para fechar o preview
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewContent('');
  };

  if (!finalFilename) return null;

  const hasToken = finalToken && finalToken.trim() !== '';
  const isDownloading = downloadingFile === finalToken;
  
  // Truncar nome do arquivo para exibição
  const displayFilename = isDownloading ? 
    `Baixando ${truncateFilename(finalFilename)}...` : 
    truncateFilename(finalFilename);
  
  // Nome completo para o tooltip
  const fullDisplayName = isDownloading ? 
    `Baixando ${finalFilename}...` : 
    finalFilename;

  const handlePreviewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loadingPreview) return;
    
    handlePreview(finalHtmlPreviewToken, finalFilename);
  };

  const handleClick = (e) => {
    if (!hasToken) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (isDownloading) return;
    
    handleDownload(finalToken, finalFilename);
  };

  return (
    <>
      {/* Alerta flutuante */}
      {showError && error && (
        <Alert 
          severity="error" 
          className={classes.floatingAlert}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      
      {/* Modal de Preview */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        className={classes.previewDialog}
        maxWidth={false}
      >
        <DialogTitle>
          Preview: {finalFilename}
        </DialogTitle>
        <DialogContent className={classes.previewContent}>
          {loadingPreview ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <iframe
              srcDoc={previewContent}
              title={`Preview de ${finalFilename}`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Item do arquivo */}
      <Box
        className={hasToken ? classes.fileItem : classes.fileItemNoLink}
        onClick={handleClick}
        style={{ 
          cursor: hasToken ? (isDownloading ? 'not-allowed' : 'pointer') : 'default',
          opacity: isDownloading ? 0.6 : 1,
          ...style
        }}
      >
        {/* Ícone de Preview (se disponível) */}
        {finalHtmlPreviewToken && (
          <Tooltip title="Visualizar Preview" placement="top" arrow>
            <VisibilityIcon 
              className={classes.previewIcon}
              onClick={handlePreviewClick}
            />
          </Tooltip>
        )}
        
        {getFileIcon(finalFilename)}
        
        <Tooltip title={fullDisplayName} placement="top" arrow>
          <Typography
            component="span"
            className={hasToken ? classes.fileName : classes.fileNameNoLink}
            style={{ 
              color: hasToken && isDownloading ? 'var(--ikaro-gray-dark)' : undefined 
            }}
          >
            {displayFilename}
          </Typography>
        </Tooltip>
      </Box>
    </>
  );
};

export default FileDownload;